import { useState } from 'react';
import styled from '@emotion/styled';
import Textarea from 'components/forms/TextArea';
import Input from 'components/forms/Input';
import Button from 'components/ui/Button';
import { useContent } from 'context/ContentContext';
import { reportError } from 'utils/reportError';

const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]))
    .join('&');
};

const StyledForm = styled.form`
  width: 100%;
  max-width: 422px;
  position: relative;
  z-index: 1;
`;

const ContactForm = () => {
  const { messages = {} } = useContent();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null); // "success" or "error"

  // Fixed handleChange function with proper error checking
  const handleChange = (e) => {
    if (!e || !e.target) return;

    const { name, value } = e.target;
    if (name) {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      message: '',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({ 'form-name': 'contact-form', ...formData }),
    })
      .then((response) => {
        if (response.ok) {
          resetForm();
          setSubmissionStatus('success');
        } else {
          const error = new Error(`HTTP status ${response.status}`);
          error.status = response.status;
          throw error;
        }
      })
      .catch((error) => {
        // A lost customer message is invisible unless it is reported (#479).
        // Only the status goes along — never the form contents.
        reportError(error, {
          component: 'ContactForm',
          action: 'submit',
          extra: { status: error.status ?? null },
        });
        setSubmissionStatus('error');
      })
      .finally(() => setIsSubmitting(false));
  };

  const StyledButton = styled(Button)`
    width: 100%;
  `;

  return (
    <StyledForm
      name="contact-form"
      aria-labelledby="contact-form"
      method="post"
      data-netlify="true"
      onSubmit={handleSubmit}
    >
      <input type="hidden" name="form-name" value="contact-form" />
      <Input
        variant="filled"
        type="text"
        name="name"
        aria-label={messages.contactForm?.nameLabel || 'Name'}
        placeholder={messages.contactForm?.namePlaceholder || 'Name'}
        value={formData.name}
        onChange={handleChange}
      />
      <Input
        variant="filled"
        type="email"
        name="email"
        aria-label={messages.contactForm?.emailLabel || 'Email'}
        placeholder={messages.contactForm?.emailPlaceholder || 'Email'}
        value={formData.email}
        onChange={handleChange}
      />
      <Textarea
        name="message"
        label={messages.contactForm?.messageLabel || 'Message'}
        maxLength={250}
        placeholder={messages.contactForm?.messagePlaceholder || 'Message'}
        value={formData.message}
        onChange={handleChange}
      />
      <StyledButton
        type="submit"
        disabled={isSubmitting}
        defaultColor="green"
        hoverColor="red"
      >
        {isSubmitting
          ? messages.contactForm?.sending || 'Sending...'
          : submissionStatus === 'success'
            ? messages.contactForm?.success || 'Message Sent!'
            : submissionStatus === 'error'
              ? messages.contactForm?.error || 'Error. Try Again'
              : messages.contactForm?.submit || 'Send Message'}
      </StyledButton>
    </StyledForm>
  );
};

export default ContactForm;
